'use client'

import ErrorPage from '@/components/error-page'
import { CustomError } from '@/components/error-page/errors'
import { FullPageErrors } from '@/components/error-page/Errors.enum'
import { useLanguage, useLocale, useLocaleLink } from '@/components/Link'
import Navigation from '@/components/navigation'
import { RichRating } from '@/components/page-specific/catering/supplier-menu/components/rich-rating/Component'
import { VendorHeader } from '@/components/page-specific/catering/supplier-menu/components/vendor-header'
import {
  isCateringTeamOrderEntrySettled,
  useCateringTeamOrderEntry,
} from '@/components/page-specific/catering/supplier-menu/states/catering-team-order-entry'
import { useMenuPageVendor } from '@/components/page-specific/catering/supplier-menu/states/useMenuPageVendor'
import {
  useCartPreview,
  useCartSnackbar,
  useCartSnackbarActions,
  useCartStore,
} from '@/components/page-specific/catering/supplier-menu/store/cartStore'
import PlatformAlertBanner from '@/components/PlatformAlertBanner'
import { PortalType, type GetVendorQuery } from '@/generated/graphql'
import { Box, Alert, AlertTitle, Snackbar, Stack, Container } from '@mui/material'

import type React from 'react'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { ExistingOrderController } from './ExistingOrderController'
import { SidebarAndMenuSkeleton } from './MainContentSkeleton'

const SIDEBAR_WIDTH = 280

type Props = {
  vendorDetails: NonNullable<GetVendorQuery['vendor']>
  lang: string
  children: React.ReactNode
}

const MarketplaceMismatch: React.FC<{ redirectHref: string }> = ({ redirectHref }) => {
  const marketplaceError = new Error('SUPPLIER_DIFFERENT_MARKETPLACE') as CustomError
  marketplaceError.errorMessage = FullPageErrors.SUPPLIER_DIFFERENT_MARKETPLACE
  marketplaceError.ctaHref = redirectHref
  marketplaceError.ctaLabel = 'Take me back'

  return (
    <ErrorPage
      error={marketplaceError}
      errorMessage={marketplaceError.errorMessage}
      showSearchVendorsCta={false}
      showEditBasketCta={false}
      showViewBasketCta={false}
      onChatToUsClick={() => {}}
    />
  )
}

export function SupplierPageShell({ vendorDetails, lang, children }: Props) {
  const { t } = useTranslation()
  const { setMobileBasketOpen } = useCartStore()
  const snackbar = useCartSnackbar()
  const { hideSnackbar } = useCartSnackbarActions()
  const { itemQuantity, grandTotal, newItemWasJustAdded } = useCartPreview()
  const cateringTeamOrderEntry = useCateringTeamOrderEntry()
  const language = useLanguage()
  const locale = useLocale()
  const getLocaleLink = useLocaleLink()

  const { vendor } = useMenuPageVendor({ vendorDetails, language })

  // Check marketplace mismatch
  const vendorCountries = (vendorDetails?.locations ?? [])
    .map((location) => location?.country?.toUpperCase())
    .filter((country): country is string => Boolean(country))

  const localeCandidate = (locale || lang)?.toLowerCase()
  const normalizedLocale =
    localeCandidate && localeCandidate.includes('-') ? localeCandidate : undefined
  const marketplaceCountry = normalizedLocale
    ? normalizedLocale.split('-')[1]?.toUpperCase()
    : undefined
  const shouldShowMarketplaceError =
    Boolean(marketplaceCountry) &&
    vendorCountries.length > 0 &&
    marketplaceCountry &&
    !vendorCountries.includes(marketplaceCountry)

  if (shouldShowMarketplaceError) {
    return <MarketplaceMismatch redirectHref={getLocaleLink('/office-catering/vendors')} />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Suspense fallback={null}>
        <ExistingOrderController vendorId={vendorDetails?.id} />
      </Suspense>
      <RichRating vendor={vendor} />
      <PlatformAlertBanner portalType={PortalType.Catering} />

      <Navigation
        onClickBasketPreview={() => {
          setMobileBasketOpen(true)
        }}
        previewData={{
          itemQuantity,
          grandTotal,
        }}
        newItemWasJustAdded={newItemWasJustAdded}
      />
      <Box
        sx={{
          display: 'grid',
          flex: 1,
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: `${SIDEBAR_WIDTH}px minmax(0, 1fr)`,
          },
          gridTemplateRows: 'auto 1fr',
          gridTemplateAreas: {
            xs: `
              "header"
              "content"
            `,
            lg: `
              "sidebar header"
              "sidebar content"
            `,
          },
        }}>
        <Box
          sx={{
            gridArea: 'header',
            p: { xs: 2, md: 2 },
          }}>
          <Container disableGutters sx={{ maxWidth: { xs: 'lg', xl: 'xl' }, ml: 0 }}>
            {vendor && <VendorHeader vendor={vendor} rating={vendor.rating} />}
          </Container>
        </Box>

        {/* Share-link visits hold the menu until the redirect fires or the basket is cached, so
            the cart mounts already knowing it is a group basket — one paint, no re-flow. */}
        {isCateringTeamOrderEntrySettled(cateringTeamOrderEntry) ? (
          children
        ) : (
          <SidebarAndMenuSkeleton />
        )}
      </Box>

      <Snackbar
        open={Boolean(snackbar.type)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={snackbar.autoHideDuration}
        sx={{ marginBottom: '60px' }}
        onClose={(_e: React.SyntheticEvent | Event, reason?: string) => {
          if (reason === 'clickaway') {
            return
          }
          hideSnackbar()
        }}>
        <Stack>
          {match(snackbar.type)
            .with('standingOrderSaved', () => (
              <Alert severity="success" variant="filled" sx={{ zIndex: 4 }}>
                <AlertTitle sx={{ marginBottom: 0 }}>
                  {snackbar.message || t('marketplace:standingOrderSaved')}
                </AlertTitle>
              </Alert>
            ))
            .with('linkCopied', () => (
              <Alert severity="success" variant="filled" sx={{ zIndex: 4 }}>
                <AlertTitle sx={{ marginBottom: 0 }}>
                  {snackbar.message || t('marketplace:linkCopiedToClipboard')}
                </AlertTitle>
              </Alert>
            ))
            .with('itemAdded', () => (
              <Alert severity="success" variant="filled" sx={{ zIndex: 4 }}>
                <AlertTitle sx={{ marginBottom: 0 }}>
                  {snackbar.message || 'Item added to basket'}
                </AlertTitle>
              </Alert>
            ))
            .with('itemRemoved', () => (
              <Alert severity="info" variant="filled" sx={{ zIndex: 4 }}>
                <AlertTitle sx={{ marginBottom: 0 }}>
                  {snackbar.message || 'Item removed from basket'}
                </AlertTitle>
              </Alert>
            ))
            .with('error', () => (
              <Alert severity="error" variant="filled" sx={{ zIndex: 4 }}>
                <AlertTitle sx={{ marginBottom: 0 }}>
                  {snackbar.message || 'An error occurred'}
                </AlertTitle>
              </Alert>
            ))
            .otherwise(() => null)}
        </Stack>
      </Snackbar>
    </Box>
  )
}
